import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdDelete, MdArrowBack, MdCloudUpload } from 'react-icons/md';
import { useVideos } from '../../context/VideosContext';
import { ORIGINAL_YOUTUBE_VIDEOS } from '../../utils/originalYoutubeVideos';
import './Admin.css';

const AdminVideos = () => {
    const navigate = useNavigate();
    const { videos, addVideo, deleteVideo, loading } = useVideos();

    const [platform, setPlatform] = useState('instagram');
    const [category, setCategory] = useState('video');
    const [title, setTitle] = useState('');
    const [url, setUrl] = useState('');
    const [saving, setSaving] = useState(false);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState('');

    const handleAdd = async (e) => {
        e.preventDefault();
        setError('');

        if (!url.trim()) {
            setError('Please paste a video URL.');
            return;
        }

        setSaving(true);
        try {
            await addVideo({
                platform,
                category: platform === 'youtube' ? category : 'reel',
                title: title.trim() || 'Untitled',
                url: url.trim()
            });
            setTitle('');
            setUrl('');
        } catch (err) {
            setError('Failed to add video: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (video) => {
        if (!window.confirm(`Delete "${video.title}"?`)) return;
        await deleteVideo(video.id);
    };

    const handleImportOriginal = async () => {
        const existingUrls = new Set(videos.map((v) => v.url));
        const toImport = ORIGINAL_YOUTUBE_VIDEOS.filter((v) => !existingUrls.has(v.url));

        if (toImport.length === 0) {
            setError('');
            window.alert('Those videos are already added.');
            return;
        }

        setImporting(true);
        try {
            for (const video of toImport) {
                await addVideo({ platform: 'youtube', category: video.category, title: video.title, url: video.url });
            }
        } catch (err) {
            setError('Failed to import videos: ' + err.message);
        } finally {
            setImporting(false);
        }
    };

    let videosContent = (
        <ul className="admin-list">
            {videos.map((video) => (
                <li key={video.id}>
                    <span>
                        [{video.platform}{video.platform === 'youtube' ? ` · ${video.category}` : ''}] {video.title}
                    </span>
                    <button type="button" onClick={() => handleDelete(video)} aria-label="Delete video">
                        <MdDelete size={16} />
                    </button>
                </li>
            ))}
        </ul>
    );
    if (loading) videosContent = <p>Loading...</p>;
    else if (videos.length === 0) videosContent = <p>No videos added yet.</p>;

    return (
        <div className="admin-page">
            <button type="button" className="admin-back-btn" onClick={() => navigate('/admin')}>
                <MdArrowBack size={20} /> Back to Dashboard
            </button>

            <div className="admin-card">
                <h1>Manage Videos</h1>
                <p className="admin-subtitle">Add Instagram Reels or YouTube videos to show on the Videos page</p>

                {error && <div className="admin-error">{error}</div>}

                <div className="admin-toolbar">
                    <button type="button" className="admin-btn admin-btn-outline" onClick={handleImportOriginal} disabled={importing}>
                        <MdCloudUpload size={18} /> {importing ? 'Importing...' : 'Import Original YouTube Videos'}
                    </button>
                </div>

                <form onSubmit={handleAdd} className="admin-form">
                    <div className="admin-form-row">
                        <label className="admin-field">
                            <span>Platform</span>
                            <select value={platform} onChange={(e) => setPlatform(e.target.value)}>
                                <option value="instagram">Instagram</option>
                                <option value="youtube">YouTube</option>
                            </select>
                        </label>

                        {platform === 'youtube' && (
                            <label className="admin-field">
                                <span>Type</span>
                                <select value={category} onChange={(e) => setCategory(e.target.value)}>
                                    <option value="video">Video</option>
                                    <option value="short">Shorts</option>
                                </select>
                            </label>
                        )}
                    </div>

                    <label className="admin-field">
                        <span>Title</span>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="e.g. New Sambalpuri Collection"
                        />
                    </label>

                    <label className="admin-field">
                        <span>{platform === 'instagram' ? 'Instagram Reel/Post URL' : 'YouTube Video URL'}</span>
                        <input
                            type="text"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder={
                                platform === 'instagram'
                                    ? 'https://www.instagram.com/reel/XXXXXXXXXXX/'
                                    : 'https://www.youtube.com/watch?v=XXXXXXXXXXX'
                            }
                            required
                        />
                    </label>

                    <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                        {saving ? 'Adding...' : 'Add Video'}
                    </button>
                </form>
            </div>

            <div className="admin-card" style={{ marginTop: 20 }}>
                <h2>Existing Videos</h2>
                {videosContent}
            </div>
        </div>
    );
};

export default AdminVideos;
